import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { WORLD_CUP_WELCOME_CIRCLE_COUNT } from './WorldCupWelcomeScreen.constants';

export const getWelcomeSplashPhaseStyles = (theme: Theme, useIosAppStoreBranding: boolean) =>
  StyleSheet.create({
    container: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: theme.spacing.xl,
    },
    circlesLayer: {
      ...StyleSheet.absoluteFillObject,
      overflow: 'hidden',
    },
    iconWrap: {
      width: 88,
      height: 88,
      borderRadius: 22,
      backgroundColor: theme.primary,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing.xxl,
      ...(Platform.OS === 'web'
        ? ({ boxShadow: `0 0 32px ${theme.primary}66` } as never)
        : {
            shadowColor: theme.primary,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 20,
            elevation: 12,
          }),
    },
    iconRing: {
      position: 'absolute',
      top: -8,
      left: -8,
      right: -8,
      bottom: -8,
      borderRadius: 30,
      borderWidth: 2,
      borderColor: `${theme.primary}4D`,
    },
    title: {
      fontSize: useIosAppStoreBranding ? 28 : 32,
      fontWeight: '900',
      letterSpacing: useIosAppStoreBranding ? 0.5 : 4,
      color: theme.black,
      textAlign: 'center',
    },
    subtitle: {
      fontSize: theme.typography.body.fontSize,
      fontWeight: '500',
      color: theme.gray[600],
      textAlign: 'center',
      marginTop: theme.spacing.xs,
    },
    tagline: {
      fontSize: theme.typography.caption.fontSize,
      color: theme.gray[500],
      textAlign: 'center',
      marginTop: useIosAppStoreBranding ? theme.spacing.md : theme.spacing.sm,
      maxWidth: 260,
    },
    hostFlagsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.md,
      marginTop: theme.spacing.xl,
    },
    hostFlagSlot: {
      borderRadius: theme.borderRadius.sm,
      overflow: 'hidden',
    },
    button: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.xxl + theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.xxl,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.primary,
      overflow: 'hidden',
    },
    buttonPressed: {
      opacity: 0.88,
      transform: [{ scale: 0.98 }],
    },
    buttonHovered: {
      opacity: 0.9,
    },
    buttonLabel: {
      fontSize: theme.typography.label.fontSize,
      fontWeight: '700',
      color: theme.onPrimary,
    },
  });

/** Non-animated base style for each background circle. Compute once via useMemo. */
export const getCircleBaseStyles = (theme: Theme) =>
  Array.from({ length: WORLD_CUP_WELCOME_CIRCLE_COUNT }, (_, i) => {
    const size = 100 + i * 40;
    return {
      position: 'absolute' as const,
      width: size,
      height: size,
      borderRadius: size / 2,
      backgroundColor: i % 2 === 0 ? theme.primary : theme.accent,
      left: `${15 + i * 12}%` as never,
      top: `${10 + (i % 3) * 25}%` as never,
    };
  });
